/** At most ten years of prepaid time on one record: anything above is a unit mistake, not a balance. */
export const MAX_IMPORTED_SECONDS = 10 * 365 * 24 * 3600;
const UNIT_SECONDS = { seconds: 1n, minutes: 60n, hours: 3600n };
/**
 * Read a time balance in `unit`, exactly. A decimal is allowed only when it comes to whole seconds ("1.5" hours is
 * 5400 s; "0.33" minutes is refused). A negative time balance is read as negative; the validator decides about it.
 */
export function parseDurationText(text, unit) {
    if (text === null || text === undefined)
        return { kind: 'empty' };
    let rest = text.trim();
    if (rest === '')
        return { kind: 'empty' };
    let negative = false;
    if (rest.startsWith('-') || rest.startsWith('−')) {
        negative = true;
        rest = rest.slice(1).trim();
    }
    let seconds;
    if (unit === 'hms' || /^\d+:\d{1,2}(:\d{1,2})?$/.test(rest)) {
        const match = /^(\d+):(\d{1,2})(?::(\d{1,2}))?$/.exec(rest);
        if (match === null)
            return { kind: 'error', reason: 'not-a-duration' };
        const hours = BigInt(match[1] ?? '0');
        const minutes = BigInt(match[2] ?? '0');
        const secs = BigInt(match[3] ?? '0');
        if (minutes > 59n || secs > 59n)
            return { kind: 'error', reason: 'not-a-duration' };
        seconds = hours * 3600n + minutes * 60n + secs;
    }
    else {
        const match = /^(\d+)(?:\.(\d+))?$/.exec(rest.replace(/,/g, ''));
        if (match === null)
            return { kind: 'error', reason: 'not-a-duration' };
        const whole = BigInt(match[1] ?? '0');
        const fraction = match[2] ?? '';
        const scale = 10n ** BigInt(fraction.length);
        const numerator = (whole * scale + (fraction === '' ? 0n : BigInt(fraction))) * UNIT_SECONDS[unit];
        if (numerator % scale !== 0n)
            return { kind: 'error', reason: 'not-whole-seconds' };
        seconds = numerator / scale;
    }
    if (seconds > BigInt(MAX_IMPORTED_SECONDS))
        return { kind: 'error', reason: 'too-large' };
    const value = Number(seconds);
    return { kind: 'ok', seconds: negative && value !== 0 ? -value : value };
}
function isLeap(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
function calendarDate(year, month, day) {
    if (year < 1900 || year > 2200 || month < 1 || month > 12 || day < 1)
        return null;
    const days = [31, isLeap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 0;
    if (day > days)
        return null;
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/** Days from 1899-12-30 (Excel's 1900 date system, its leap-year bug included) to a calendar date. */
function excelSerialDate(serial) {
    if (!Number.isInteger(serial) || serial < 1 || serial > 120_000)
        return null;
    const epoch = Date.UTC(1899, 11, 30);
    const instant = new Date(epoch + serial * 86_400_000);
    return calendarDate(instant.getUTCFullYear(), instant.getUTCMonth() + 1, instant.getUTCDate());
}
/**
 * Read a calendar date: ISO `YYYY-MM-DD` (with or without a time after it, which is dropped — a birthday or a join
 * date is a day), or `a/b/c` / `a-b-c` / `a.b.c` in `order`, or — from a spreadsheet — an Excel serial day number.
 * Two-digit years are refused: "01/02/03" is not a fact anyone should have to guess.
 */
export function parseDateText(text, order, options = {}) {
    if (text === null || text === undefined)
        return { kind: 'empty' };
    const rest = text.trim();
    if (rest === '')
        return { kind: 'empty' };
    const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ][0-9:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.exec(rest);
    if (iso !== null) {
        const date = calendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
        return date === null ? { kind: 'error' } : { kind: 'ok', date };
    }
    if (options.allowSerial === true && /^\d{1,6}(\.\d+)?$/.test(rest)) {
        // A date-time serial (45123.75) is the same day: the fraction is the time of day, dropped.
        const date = excelSerialDate(Number(rest.split('.')[0]));
        return date === null ? { kind: 'error' } : { kind: 'ok', date };
    }
    const parts = /^(\d{1,4})[/.-](\d{1,2})[/.-](\d{1,4})(?:[ T][0-9:.]+(?:\s?[AaPp][Mm])?)?$/.exec(rest);
    if (parts === null)
        return { kind: 'error' };
    const [a, b, c] = [parts[1] ?? '', parts[2] ?? '', parts[3] ?? ''];
    let year;
    let month;
    let day;
    if (order === 'ymd')
        [year, month, day] = [a, b, c];
    else if (order === 'dmy')
        [day, month, year] = [a, b, c];
    else
        [month, day, year] = [a, b, c];
    if (year.length !== 4)
        return { kind: 'error' };
    const date = calendarDate(Number(year), Number(month), Number(day));
    return date === null ? { kind: 'error' } : { kind: 'ok', date };
}
// ---------------------------------------------------------------------------------------------
// Phone numbers
// ---------------------------------------------------------------------------------------------
/** The country calling codes a venue's region implies for a national number (spec/03 §10). */
const NATIONAL_PREFIX = {
    IN: { code: '91', nationalDigits: 10 },
};
/**
 * Normalise a phone number to E.164 with the venue's region for national numbers (`+91` in India): "98450 12345",
 * "098450-12345", "+91 98450 12345" and "919845012345" are all `+919845012345`. A number that does not parse is an
 * error, and the validator drops it with a warning — it is a claim aid, never an identity.
 */
export function parsePhoneText(text, region) {
    if (text === null || text === undefined)
        return { kind: 'empty' };
    const rest = text.trim();
    if (rest === '')
        return { kind: 'empty' };
    if (!/^[+\d\s().-]+$/.test(rest))
        return { kind: 'error' };
    const plus = rest.startsWith('+') || rest.startsWith('00');
    let digits = rest.replace(/\D/g, '');
    if (rest.startsWith('00'))
        digits = digits.slice(2);
    const national = NATIONAL_PREFIX[region.toUpperCase()];
    if (!plus && national !== undefined) {
        if (digits.length === national.nationalDigits + 1 && digits.startsWith('0'))
            digits = digits.slice(1);
        if (digits.length === national.nationalDigits)
            digits = national.code + digits;
        else if (!(digits.length === national.code.length + national.nationalDigits && digits.startsWith(national.code)))
            return { kind: 'error' };
    }
    if (national !== undefined && digits.startsWith(national.code) && region.toUpperCase() === 'IN') {
        // An Indian mobile or landline number is ten digits after +91, never starting with 0.
        const local = digits.slice(national.code.length);
        if (local.length !== national.nationalDigits || local.startsWith('0'))
            return { kind: 'error' };
    }
    if (!/^[1-9]\d{7,14}$/.test(digits))
        return { kind: 'error' };
    return { kind: 'ok', e164: `+${digits}` };
}
const EMAIL_PATTERN = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
/**
 * An email, lower-cased and trimmed. Malformed is an error; an address at the reserved `.invalid`, `.example`,
 * `.test` or `.localhost` names (a placeholder some systems write for "no email") is a `placeholder`. Neither is
 * imported as a claim channel: the player claims at the counter instead.
 */
export function parseEmailText(text) {
    if (text === null || text === undefined)
        return { kind: 'empty' };
    const email = text.trim().toLowerCase();
    if (email === '')
        return { kind: 'empty' };
    if (email.length > 254 || !EMAIL_PATTERN.test(email))
        return { kind: 'error', reason: 'malformed' };
    const tld = email.slice(email.lastIndexOf('.') + 1);
    if (['invalid', 'example', 'test', 'localhost'].includes(tld))
        return { kind: 'error', reason: 'placeholder' };
    return { kind: 'ok', email };
}
// ---------------------------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------------------------
/** Read a yes/no cell: yes, y, true, 1, banned, locked, deleted, disabled → true; no, n, false, 0, active, "" → false. */
export function parseFlagText(text) {
    if (text === null || text === undefined)
        return false;
    const value = text.trim().toLowerCase();
    if (['', 'no', 'n', 'false', '0', 'active', 'enabled', 'ok'].includes(value))
        return false;
    if (['yes', 'y', 'true', '1', 'banned', 'locked', 'blocked', 'deleted', 'disabled', 'inactive'].includes(value))
        return true;
    return null;
}
//# sourceMappingURL=fields.js.map